/* eslint-disable camelcase */
import React, { useState, useMemo } from 'react';
import LegacyPratusTable from '../../../DesignSystems/PratusTable/LegacyPratusTable';
import { StylishNewButton } from '../../../DesignSystems/New/StylishNewButton';
import { Badge, Form } from 'react-bootstrap';
import Loading from '../../../Loading/Loading';
import StatusUpdateDrawer from '../Drawers/StatusUpdateDrawer';
import { ResourceInIncident } from '../../hooks/resourceInIncidentHooks';
import { useWorkAssignments } from '../../hooks/workAssignmentHooks';
import { STATUS_CONFIG, ResourceStatus } from '../constants/resourceConstants';
import { getStatusColor } from '../utils/statusUtils';
import { formatDateTime } from '../utils/dateUtils';
import StylishNewSelect from '../../../DesignSystems/New/StylishNewSelect';

// Type for resource kind filter (includes 'all' for no filter)
type ResourceKindFilter = string | 'all';

interface CheckInTableProps {
  resources: ResourceInIncident[];
  isLoading?: boolean;
  onStatusUpdate?: (resource: ResourceInIncident) => void;
  onExportCheckedIn?: () => void;
}

const CheckInTable: React.FC<CheckInTableProps> = ({
  resources,
  isLoading,
  onStatusUpdate,
  onExportCheckedIn,
}) => {
  const { data: workAssignments = [] } = useWorkAssignments();
  const [selectedResource, setSelectedResource] =
    useState<ResourceInIncident | null>(null);
  const [showStatusDrawer, setShowStatusDrawer] = useState(false);
  const [showCurrentOnly, setShowCurrentOnly] = useState(true);
  const [selectedKind, setSelectedKind] = useState<ResourceKindFilter>('all');
  const [searchText, setSearchText] = useState<string>('');

  // Get unique kinds and filter resources
  const { availableKinds, filteredResources } = useMemo(() => {
    const kinds = new Set<string>();

    // First filter to only include resources that have been checked in
    // and exclude in_transit resources
    let filtered = resources.filter(
      (resource) =>
        resource.checkin_datetime && resource.current_status !== 'in_transit'
    );

    // Collect kinds from all checked-in resources
    filtered.forEach((resource) => {
      if (resource.resource_kind) kinds.add(resource.resource_kind);
    });

    if (showCurrentOnly) {
      // Show only current resources (checked in but not checked out)
      filtered = filtered.filter((resource) => !resource.checkout_datetime);
    }

    // Filter by kind
    if (selectedKind && selectedKind !== 'all') {
      filtered = filtered.filter((r) => r.resource_kind === selectedKind);
    }

    // Filter by search
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase().trim();
      filtered = filtered.filter((resource) => {
        const searchableText = [
          resource.resource_name || '',
          resource.resource_name_identifier || '',
          resource.resource_kind || '',
          resource.resource_type || '',
          resource.leader_name || '',
          resource.home_unit_agency || '',
          resource.order_request_number || '',
        ]
          .join(' ')
          .toLowerCase();
        return searchableText.includes(searchLower);
      });
    }

    return {
      availableKinds: Array.from(kinds).sort(),
      filteredResources: filtered,
    };
  }, [resources, showCurrentOnly, selectedKind, searchText]);

  if (isLoading) {
    return <Loading />;
  }

  const getStatusBadge = (status: keyof typeof STATUS_CONFIG) => {
    const config = STATUS_CONFIG[status];
    const color = config ? getStatusColor(status) : 'secondary';
    const label = config ? config.label : status;

    return <Badge bg={color}>{label}</Badge>;
  };

  const handleStatusUpdate = (resource: ResourceInIncident) => {
    setSelectedResource(resource);
    setShowStatusDrawer(true);
  };

  const columns = [
    {
      dataField: 'checkin_datetime',
      text: 'Check-In',
      sort: true,
      formatter: (cell: string) => formatDateTime(cell),
    },
    {
      dataField: 'resource_classification',
      text: 'Resource Classification',
      sort: true,
      formatter: (cell: string, row: ResourceInIncident) => (
        <div>
          <strong>
            {row.resource_name_identifier || row.resource_name || 'Unknown'}
          </strong>
          <br />
          <small className="text-muted">
            {[
              row.state_and_agency,
              row.resource_category,
              row.resource_kind,
              row.resource_type,
              row.strike_team_task_force,
            ]
              .filter(Boolean)
              .join(' | ')}
          </small>
        </div>
      ),
    },
    {
      dataField: 'order_request_number',
      text: 'Order Request #',
      sort: true,
      formatter: (cell: string) => cell || '-',
    },
    {
      dataField: 'leader_name',
      text: 'Leader',
      sort: true,
      formatter: (cell: string, row: ResourceInIncident) => (
        <div>
          {row.leader_name || '-'}
          <br />
          <small className="text-muted">
            {row.personnel_count || 0} personnel
          </small>
        </div>
      ),
    },
    {
      dataField: 'home_unit_agency',
      text: 'Home Unit/Agency',
      formatter: (cell: string) => cell || '-',
    },
    {
      dataField: 'current_status',
      text: 'Status',
      sort: true,
      formatter: (cell: string) => getStatusBadge(cell as ResourceStatus),
    },
    {
      dataField: 'incident_assignment_location',
      text: 'Assignment',
      formatter: (cell: string, row: ResourceInIncident) => {
        if (row.current_status === 'assigned' && row.work_assignment_id) {
          const workAssignment = workAssignments.find(
            (wa) => wa.id === row.work_assignment_id
          );
          if (workAssignment) {
            return (
              workAssignment.work_assignment ||
              `Assignment ${workAssignments.indexOf(workAssignment) + 1}`
            );
          }
        }
        return cell || '-';
      },
    },
    {
      dataField: 'departure_point',
      text: 'Departure',
      formatter: (cell: string, row: ResourceInIncident) => (
        <div>
          <small>{row.departure_point || '-'}</small>
          {row.departure_datetime && (
            <>
              <br />
              <small className="text-muted">
                {formatDateTime(row.departure_datetime)}
              </small>
            </>
          )}
        </div>
      ),
    },
    {
      dataField: 'actions',
      text: 'Actions',
      isDummyField: true,
      formatter: (cell: string, row: ResourceInIncident) => (
        <StylishNewButton
          className="button button--primary"
          onClick={() => handleStatusUpdate(row)}
        >
          Update Status
        </StylishNewButton>
      ),
    },
  ];

  const kindOptions = [
    { value: 'all', label: 'All Kinds' },
    ...availableKinds.map((kind) => ({ value: kind, label: kind })),
  ];

  return (
    <div className="check-in-table">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>ICS 211 Check-In List</h5>
        <div className="d-flex align-items-center gap-2">
          {onExportCheckedIn && (
            <StylishNewButton
              className="button--secondary"
              onClick={onExportCheckedIn}
            >
              Preview ICS 211-CG
            </StylishNewButton>
          )}
          <span className="text-muted">
            {showCurrentOnly ? 'Current' : 'All'}
          </span>
          <Form.Switch
            id="current-resources-toggle"
            checked={showCurrentOnly}
            onChange={(e) => setShowCurrentOnly(e.target.checked)}
          />
        </div>
      </div>

      <div className="d-flex gap-3 mb-3">
        <div style={{ width: '250px' }}>
          <StylishNewSelect
            options={kindOptions}
            value={kindOptions.find((opt) => opt.value === selectedKind)}
            onChange={(option: { value: string; label: string } | null) => {
              setSelectedKind((option?.value || 'all') as ResourceKindFilter);
            }}
            placeholder="Filter by Kind"
          />
        </div>
        <div className="flex-fill">
          <Form.Control
            type="text"
            placeholder="Search resources..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
      </div>

      <LegacyPratusTable
        keyField="id"
        columns={columns}
        rows={filteredResources}
      />

      {showStatusDrawer && selectedResource && (
        <StatusUpdateDrawer
          resource={selectedResource}
          onClose={() => {
            setShowStatusDrawer(false);
            setSelectedResource(null);
          }}
          onSave={(data) => {
            onStatusUpdate?.({ ...selectedResource, ...data });
            setShowStatusDrawer(false);
            setSelectedResource(null);
          }}
        />
      )}
    </div>
  );
};

export default CheckInTable;
